/**
 * Shape factory functions for the MD3 Expressive Shape Engine.
 * Ported from androidx.graphics.shapes.Shapes.kt
 *
 * These functions create basic geometric shapes as RoundedPolygon instances.
 * All shapes default to being centered at (0,0) with radius 1.
 */

import { RoundedPolygon } from "../core/rounded-polygon";
import {
	type CornerRounding,
	cornerRounding,
	UNROUNDED,
} from "../math/corner-rounding";
import { distance, FLOAT_PI, radialToCartesian } from "../utils/utils";

/**
 * Creates a circular shape approximated by a rounded polygon.
 *
 * @param numVertices - Number of polygon vertices (≥ 3, default: 8)
 * @param radius - Circle radius (default: 1)
 * @param centerX - Center X (default: 0)
 * @param centerY - Center Y (default: 0)
 * @returns A RoundedPolygon approximating a circle
 *
 * @example
 * const myCircle = circle(8, 100, 0, 0);
 */
export function circle(
	numVertices = 8,
	radius = 1,
	centerX = 0,
	centerY = 0,
): RoundedPolygon {
	if (numVertices < 3) throw new Error("Circle must have at least 3 vertices");
	// The polygon radius must be larger so that after rounding, the result matches desired radius
	const theta = FLOAT_PI / numVertices;
	const polygonRadius = radius / Math.cos(theta);
	return RoundedPolygon.fromNumVertices(
		numVertices,
		polygonRadius,
		centerX,
		centerY,
		cornerRounding(radius),
	);
}

/**
 * Creates a rectangular shape with optional rounding.
 *
 * @param width - Rectangle width (default: 2)
 * @param height - Rectangle height (default: 2)
 * @param rounding - Corner rounding for all corners (default: none)
 * @param perVertexRounding - Per-corner rounding overrides (length must be 4)
 * @param centerX - Center X (default: 0)
 * @param centerY - Center Y (default: 0)
 *
 * @example
 * // Rounded rectangle
 * const roundedRect = rectangle(2, 1, { radius: 0.25, smoothing: 0 });
 */
export function rectangle(
	width = 2,
	height = 2,
	rounding: CornerRounding = UNROUNDED,
	perVertexRounding?: CornerRounding[],
	centerX = 0,
	centerY = 0,
): RoundedPolygon {
	const left = centerX - width / 2;
	const top = centerY - height / 2;
	const right = centerX + width / 2;
	const bottom = centerY + height / 2;
	// Vertices in clockwise order: bottom-right, bottom-left, top-left, top-right
	return RoundedPolygon.fromVertices(
		[right, bottom, left, bottom, left, top, right, top],
		rounding,
		perVertexRounding,
		centerX,
		centerY,
	);
}

/**
 * Creates a star-shaped polygon with inner and outer radii.
 *
 * If innerRadius equals radius, the result is a regular polygon with
 * 2 × numVerticesPerRadius vertices.
 *
 * @param numVerticesPerRadius - Vertices per radius (outer + inner)
 * @param radius - Outer radius (default: 1)
 * @param innerRadius - Inner radius (default: 0.5)
 * @param rounding - Corner rounding for outer vertices (default: none)
 * @param innerRounding - Corner rounding for inner vertices (default: uses rounding)
 * @param perVertexRounding - Full per-vertex rounding list
 * @param centerX - Center X (default: 0)
 * @param centerY - Center Y (default: 0)
 *
 * @example
 * // 8-pointed star
 * const star8 = star(8, 1, 0.4);
 *
 * @example
 * // Rounded sunny shape
 * const sunny = star(8, 1, 0.75, { radius: 0.15, smoothing: 0 }, { radius: 0.05, smoothing: 0 });
 */
export function star(
	numVerticesPerRadius: number,
	radius = 1,
	innerRadius = 0.5,
	rounding: CornerRounding = UNROUNDED,
	innerRounding?: CornerRounding,
	perVertexRounding?: CornerRounding[],
	centerX = 0,
	centerY = 0,
): RoundedPolygon {
	if (radius <= 0 || innerRadius <= 0)
		throw new Error("Star radii must be greater than 0");
	if (innerRadius >= radius)
		throw new Error("innerRadius must be less than radius");

	let pvRounding = perVertexRounding;
	if (!pvRounding && innerRounding) {
		const resolvedInnerRounding = innerRounding;
		pvRounding = Array.from({ length: numVerticesPerRadius }, (_, i) =>
			i % 2 === 0 ? rounding : resolvedInnerRounding,
		);
		// Flatten: alternating outer/inner for 2*n vertices
		pvRounding = [];
		for (let i = 0; i < numVerticesPerRadius; i++) {
			pvRounding.push(rounding, resolvedInnerRounding);
		}
	}

	const vertices = starVertices(
		numVerticesPerRadius,
		radius,
		innerRadius,
		centerX,
		centerY,
	);
	return RoundedPolygon.fromVertices(
		vertices,
		rounding,
		pvRounding,
		centerX,
		centerY,
	);
}

/**
 * Creates a pill shape: rectangle with fully-rounded short ends.
 *
 * @param width - Width of the pill (default: 2)
 * @param height - Height of the pill (default: 1)
 * @param smoothing - Corner smoothing factor [0,1] (default: 0)
 * @param centerX - Center X (default: 0)
 * @param centerY - Center Y (default: 0)
 *
 * @example
 * const pillShape = pill(3, 1);
 */
export function pill(
	width = 2,
	height = 1,
	smoothing = 0,
	centerX = 0,
	centerY = 0,
): RoundedPolygon {
	if (width <= 0 || height <= 0)
		throw new Error("Pill must have positive width and height");
	const wHalf = width / 2;
	const hHalf = height / 2;
	const r = Math.min(wHalf, hHalf);
	return RoundedPolygon.fromVertices(
		[
			wHalf + centerX,
			hHalf + centerY,
			-wHalf + centerX,
			hHalf + centerY,
			-wHalf + centerX,
			-hHalf + centerY,
			wHalf + centerX,
			-hHalf + centerY,
		],
		cornerRounding(r, smoothing),
		undefined,
		centerX,
		centerY,
	);
}

/**
 * Creates a pill-star shape: like a pill but with star-like inner/outer vertices.
 *
 * @param width - Width (default: 2)
 * @param height - Height (default: 1)
 * @param numVerticesPerRadius - Number of point pairs per radius
 * @param innerRadiusRatio - Inner radius as fraction of outer (default: 0.5)
 * @param rounding - Outer vertex rounding (default: none)
 * @param innerRounding - Inner vertex rounding (default: uses rounding)
 * @param perVertexRounding - Full per-vertex rounding list
 * @param vertexSpacing - Spacing factor [0,1] (default: 0.5)
 * @param centerX - Center X (default: 0)
 * @param centerY - Center Y (default: 0)
 */
export function pillStar(
	width = 2,
	height = 1,
	numVerticesPerRadius = 8,
	innerRadiusRatio = 0.5,
	rounding: CornerRounding = UNROUNDED,
	innerRounding?: CornerRounding,
	perVertexRounding?: CornerRounding[],
	vertexSpacing = 0.5,
	centerX = 0,
	centerY = 0,
): RoundedPolygon {
	if (innerRadiusRatio <= 0 || innerRadiusRatio >= 1) {
		throw new Error("innerRadiusRatio must be between 0 and 1 (exclusive)");
	}

	// Generate pill-star vertices by arranging inner/outer points on a pill outline
	const vertices = pillStarVertices(
		width,
		height,
		numVerticesPerRadius,
		innerRadiusRatio,
		vertexSpacing,
		centerX,
		centerY,
	);

	let pvRounding = perVertexRounding;
	if (!pvRounding && innerRounding) {
		const resolvedInnerRounding = innerRounding;
		pvRounding = [];
		for (let i = 0; i < numVerticesPerRadius * 2; i++) {
			pvRounding.push(i % 2 === 0 ? rounding : resolvedInnerRounding);
		}
	}

	return RoundedPolygon.fromVertices(
		vertices,
		rounding,
		pvRounding,
		centerX,
		centerY,
	);
}

// ─── Internal helpers ─────────────────────────────────────────────────────────

function starVertices(
	numVerticesPerRadius: number,
	radius: number,
	innerRadius: number,
	cx: number,
	cy: number,
): number[] {
	const result: number[] = [];
	const total = numVerticesPerRadius * 2;
	for (let i = 0; i < total; i++) {
		const r = i % 2 === 0 ? radius : innerRadius;
		const angle = (FLOAT_PI / numVerticesPerRadius) * i;
		const [rx, ry] = radialToCartesian(r, angle);
		result.push(cx + rx, cy + ry);
	}
	return result;
}

function pillStarVertices(
	width: number,
	height: number,
	numVerticesPerRadius: number,
	innerRadiusRatio: number,
	vertexSpacing: number,
	cx: number,
	cy: number,
): number[] {
	const wHalf = width / 2;
	const hHalf = height / 2;
	const r = Math.min(wHalf, hHalf);
	const result: number[] = [];
	const total = numVerticesPerRadius * 2;

	for (let i = 0; i < total; i++) {
		const angle = (FLOAT_PI / numVerticesPerRadius) * i;
		const isOuter = i % 2 === 0;
		const _radiusScale = isOuter ? 1 : innerRadiusRatio;

		// Project angle onto pill outline
		const cos = Math.cos(angle),
			sin = Math.sin(angle);
		const baseR = isOuter ? r : r * innerRadiusRatio;

		// Interpolate between circle and rectangle based on aspect ratio
		const rx = cos * wHalf;
		const ry = sin * hHalf;
		const pillR = distance(rx, ry);
		const finalR = baseR * vertexSpacing + pillR * (1 - vertexSpacing);

		result.push(cx + cos * finalR, cy + sin * finalR);
	}
	return result;
}
